/**
 * As peças do cinema.
 *
 * Porte de `lib/features/cinema/presentation/widgets/netflix/`: a barra do
 * topo, a barra de baixo, o cartaz, o número do Top 10, o cartão de "continuar
 * assistindo", a fileira, o cartaz gigante e a espreitada do computador.
 *
 * Nenhuma medida daqui foi escolhida: todas saem de um widget de lá, e o
 * comentário diz qual.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CHAVE_TMDB_PADRAO,
  legendaDe,
  progressoDe,
  traduzir,
  type Cartaz,
  type TMDB,
} from '@nosso-sempre/shared';
import { Icone } from '../../componentes/Icone';
import { usarTocador } from '../../nucleo/tocador';

/**
 * A chave do TMDB. É a mesma que o Flutter já leva no pacote
 * (`env_config.dart`): uma chave v3 de leitura, sem conta atrás. Um proxy no
 * servidor seria mais bonito, mas a rota não existe lá — e sem chave o cinema
 * fica escuro nos dois apps por motivos diferentes.
 *
 * Mora aqui, e não na tela do cinema, porque o tocador abre sozinho por link
 * direto e precisa da mesma chave sem depender de quem o abriu.
 */
export const CHAVE = import.meta.env.VITE_TMDB_KEY || CHAVE_TMDB_PADRAO;

/** O cliente do TMDB desce por contexto: a fileira não deveria conhecê-lo. */
export const ContextoTMDB = createContext<TMDB | null>(null);

const noComputador = () => typeof window !== 'undefined' && window.innerWidth >= 1024;

/** `_matchPercent`: sem nota, o número é estável por título. */
export function combinacao(nota: number, tmdbId: number): number {
  if (nota > 0) return Math.min(99, Math.max(50, Math.round(nota * 10)));
  return 82 + (tmdbId % 17);
}

/** `_runtime`: minutos do filme, ou o primeiro `episode_run_time` da série. */
export function duracao(detalhes: Record<string, any> | null): string | null {
  if (!detalhes) return null;
  const filme = Number(detalhes.runtime ?? 0);
  if (filme > 0) {
    const h = Math.floor(filme / 60);
    const m = filme % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  const eps = detalhes.episode_run_time;
  if (Array.isArray(eps) && eps.length > 0) {
    const r = Number(eps[0] ?? 0);
    if (r > 0) return `${r}m`;
  }
  return null;
}

/* ── Detalhes em memória ─────────────────────────────────────────────
   O mesmo `_netflixDetailCache` de lá: cada título é buscado uma vez por
   sessão, e a espreitada e o cartaz gigante dividem a mesma gaveta. */

const detalhesGuardados = new Map<string, Record<string, any>>();
const trailersGuardados = new Map<string, string | null>();

export function useDetalhes(item: Cartaz | null, ligado: boolean): Record<string, any> | null {
  const tmdb = useContext(ContextoTMDB);
  const chave = item ? `${item.tmdbId}:${item.tipo}` : '';
  const [detalhes, setDetalhes] = useState<Record<string, any> | null>(
    () => detalhesGuardados.get(chave) ?? null,
  );

  useEffect(() => {
    if (!item || !ligado || !tmdb) return;
    const guardado = detalhesGuardados.get(chave);
    if (guardado) {
      setDetalhes(guardado);
      return;
    }
    let vivo = true;
    tmdb
      .detalhes(item.tmdbId, item.tipo)
      .then((d) => {
        const valor = d ?? {};
        detalhesGuardados.set(chave, valor);
        if (vivo) setDetalhes(valor);
      })
      .catch(() => {
        detalhesGuardados.set(chave, {});
        if (vivo) setDetalhes({});
      });
    return () => {
      vivo = false;
    };
  }, [chave, item, ligado, tmdb]);

  return detalhes;
}

/* ── Barra do topo (`netflix_nav_bar.dart`) ──────────────────────────── */

export interface LinkDoTopo {
  rotulo: string;
  aba: number;
  /** Quando existe, o link abre o "Browse" já com esta opção escolhida. */
  opcao?: string;
}

interface PropsBarra {
  rolou: boolean;
  aba: number;
  links: LinkDoTopo[];
  aoIr: (l: LinkDoTopo) => void;
  aoBuscar: () => void;
  aoVoltar?: () => void;
  aoConta?: () => void;
  marca?: string;
}

export function BarraDoTopo({
  rolou,
  aba,
  links,
  aoIr,
  aoBuscar,
  aoVoltar,
  aoConta,
  marca = 'Nosso Sempre Cinema',
}: PropsBarra) {
  return (
    <nav className={`cine-barra${rolou ? ' rolou' : ''}`} aria-label={traduzir('Cinema')}>
      <button type="button" className="cine-marca" onClick={() => aoIr({ rotulo: marca, aba: 0 })}>
        <span className="cine-marca-selo">
          <Icone nome="tocar" tamanho={18} preenchido />
        </span>
        <span className="cine-marca-nome">{marca}</span>
      </button>

      <div className="cine-links">
        {links.map((l) => (
          <button
            key={`${l.rotulo}-${l.aba}-${l.opcao ?? ''}`}
            type="button"
            className={`cine-link${aba === l.aba && !l.opcao ? ' aqui' : ''}`}
            aria-current={aba === l.aba && !l.opcao ? 'true' : undefined}
            onClick={() => aoIr(l)}
          >
            {l.rotulo}
          </button>
        ))}
      </div>

      <div className="cine-acoes">
        {aoVoltar ? (
          <button type="button" className="cine-acao" onClick={aoVoltar} title={traduzir('Início')}>
            <Icone nome="voltar" tamanho={24} rotulo={traduzir('Início')} />
          </button>
        ) : null}
        <button type="button" className="cine-acao" onClick={aoBuscar} title={traduzir('Buscar')}>
          <Icone nome="busca" tamanho={24} rotulo={traduzir('Buscar')} />
        </button>
        {aoConta ? (
          <button type="button" className="cine-acao" onClick={aoConta} title={traduzir('Conta')}>
            <Icone nome="conta" tamanho={24} rotulo={traduzir('Conta')} />
          </button>
        ) : null}
      </div>
    </nav>
  );
}

/* ── Barra de baixo, no celular ──────────────────────────────────────── */

export interface AbaDeBaixo {
  rotulo: string;
  icone: 'casa' | 'busca' | 'tv-ao-vivo' | 'marcador' | 'coracao';
  aba: number;
}

export function BarraDeAbas({
  abas,
  aba,
  aoIr,
}: {
  abas: AbaDeBaixo[];
  aba: number;
  aoIr: (n: number) => void;
}) {
  return (
    <nav className="cine-abas" aria-label={traduzir('Cinema')}>
      {abas.map((a) => (
        <button
          key={a.aba}
          type="button"
          className={`cine-aba${aba === a.aba ? ' aqui' : ''}`}
          aria-current={aba === a.aba ? 'page' : undefined}
          onClick={() => aoIr(a.aba)}
        >
          <Icone nome={a.icone} tamanho={23} preenchido={aba === a.aba} />
          <span>{a.rotulo}</span>
        </button>
      ))}
    </nav>
  );
}

/* ── A espreitada do computador (`netflix_hover_preview.dart`) ───────── */

function Espreitada({
  item,
  ancora,
  aoTocar,
  aoEntrar,
  aoSair,
}: {
  item: Cartaz;
  ancora: DOMRect;
  aoTocar: () => void;
  aoEntrar: () => void;
  aoSair: () => void;
}) {
  const detalhes = useDetalhes(item, item.sinopse === '');
  const largura = Math.min(360, Math.max(300, window.innerWidth * 0.26));
  const altura = largura * 0.5625 + 178;

  // `positionHoverPreview`: flutua acima do cartaz e nunca sai da janela.
  const esq = Math.min(
    Math.max(ancora.left + ancora.width / 2 - largura / 2, 12),
    Math.max(window.innerWidth - largura - 12, 12),
  );
  const topo = Math.min(
    Math.max(ancora.top + 20 - altura, 8),
    Math.max(window.innerHeight - altura - 8, 8),
  );

  const capa =
    item.fundo ||
    (typeof detalhes?.backdrop_path === 'string' && detalhes.backdrop_path
      ? `https://image.tmdb.org/t/p/w780${detalhes.backdrop_path}`
      : '') ||
    item.poster;

  const nota = Number(detalhes?.vote_average ?? item.nota ?? 0);
  const ano =
    item.ano || String(detalhes?.release_date ?? detalhes?.first_air_date ?? '').slice(0, 4);
  const tempo = duracao(detalhes);

  let serie: string | null = null;
  if (item.tipo === 'tv') {
    const t = Number(detalhes?.number_of_seasons ?? 0);
    const e = Number(detalhes?.number_of_episodes ?? 0);
    if (t > 0 && e > 0) serie = `${t} temporada${t === 1 ? '' : 's'} · ${e} eps`;
    else if (t > 0) serie = `${t} temporada${t === 1 ? '' : 's'}`;
  }

  const sinopse =
    item.sinopse ||
    String(detalhes?.overview ?? '').trim() ||
    `${item.tipo === 'movie' ? 'Movie' : 'Series'}${ano ? ` from ${ano}` : ''}. ` +
      traduzir('Toque para ver detalhes, episódios, elenco e mais.');

  const generos: string[] = Array.isArray(detalhes?.genres)
    ? detalhes.genres.map((g: any) => String(g?.name ?? '')).filter((n: string) => n.length > 0)
    : [];

  return createPortal(
    /* `aria-hidden` porque isto é enfeite de ponteiro e mais nada: tudo o que
       está aqui dentro já está no cartaz de baixo, que é um `<button>` de
       verdade, com foco e Enter. Sem isto quem lê a tela ouviria o mesmo
       filme duas vezes, e a segunda num pedaço onde não há o que apertar.
       Nada aqui é focável, então esconder não tranca ninguém do lado de fora. */
    <div
      className="cine-espreita"
      aria-hidden="true"
      style={{ left: esq, top: topo, width: largura, height: altura }}
      onMouseEnter={aoEntrar}
      onMouseLeave={aoSair}
      onClick={aoTocar}
    >
      <div className="cine-espreita-capa">
        {capa ? <img src={capa} alt="" loading="lazy" decoding="async" /> : null}
        <span className="cine-espreita-play">
          <Icone nome="tocar" tamanho={30} preenchido />
        </span>
      </div>
      <div className="cine-espreita-corpo">
        <div className="cine-espreita-meta">
          <span className="cine-combina">{combinacao(nota, item.tmdbId)}% Match</span>
          {ano ? <span>{ano}</span> : null}
          {tempo ? <span>{tempo}</span> : null}
          {serie ? <span>{serie}</span> : null}
          <span className="cine-hd">{traduzir('HD')}</span>
        </div>
        <p className="cine-espreita-titulo">{item.titulo}</p>
        <p className="cine-espreita-sinopse">{sinopse}</p>
        {generos.length > 0 ? (
          <p className="cine-espreita-generos">{generos.slice(0, 3).join(' · ')}</p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/* ── O cartaz (`netflix_poster_card.dart`) ───────────────────────────── */

interface PropsCartaz {
  item: Cartaz;
  aoTocar: () => void;
  /** Grade: cresce menos e não sobe. */
  compacto?: boolean;
  /** Top 10: o número gigante ao lado. */
  posto?: number;
  progresso?: number | null;
  /** A espreitada só existe no computador, e nem toda lista a quer. */
  espreitar?: boolean;
}

export function CartazDoFilme({
  item,
  aoTocar,
  compacto,
  posto,
  progresso,
  espreitar = true,
}: PropsCartaz) {
  const ref = useRef<HTMLButtonElement>(null);
  const relogio = useRef<number | null>(null);
  const dentroDaEspreita = useRef(false);
  const [ancora, setAncora] = useState<DOMRect | null>(null);

  const fechar = useCallback(() => {
    if (relogio.current) window.clearTimeout(relogio.current);
    relogio.current = null;
    setAncora(null);
  }, []);

  useEffect(
    () => () => {
      if (relogio.current) window.clearTimeout(relogio.current);
    },
    [],
  );

  /* A espreitada cobre os cartazes vizinhos, e quem a abriu sem querer só
     conseguia fechá-la tirando o ponteiro dali — o que, num zoom de 400%,
     quer dizer arrastar a tela inteira. O Esc fecha sem mover nada.
     (WCAG 1.4.13; o Flutter de lá também não fecha, e aqui fecha.) */
  useEffect(() => {
    if (ancora == null) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ancora, fechar]);

  const entrou = () => {
    if (!espreitar || !noComputador()) return;
    if (relogio.current) window.clearTimeout(relogio.current);
    // 260ms de espera: passar o ponteiro por cima a caminho de outro cartaz
    // não deve abrir nada.
    relogio.current = window.setTimeout(() => {
      const caixa = ref.current?.getBoundingClientRect();
      if (caixa) setAncora(caixa);
    }, 260);
  };

  const saiu = () => {
    if (relogio.current) window.clearTimeout(relogio.current);
    relogio.current = null;
    // Sair do cartaz para dentro da espreitada não a fecha.
    window.setTimeout(() => {
      if (!dentroDaEspreita.current) setAncora(null);
    }, 60);
  };

  const carta = (
    <button
      ref={ref}
      type="button"
      className={`cine-carta${compacto ? ' compacta' : ''}`}
      onClick={aoTocar}
      onMouseEnter={entrou}
      onMouseLeave={saiu}
      title={item.titulo}
    >
      {item.poster ? (
        <img src={item.poster} alt={item.titulo} loading="lazy" decoding="async" />
      ) : (
        <span className="cine-sem-cartaz">
          <span>{item.titulo}</span>
        </span>
      )}
      {progresso != null ? (
        <span className="cine-progresso">
          <i style={{ width: `${Math.min(1, Math.max(0, progresso)) * 100}%` }} />
        </span>
      ) : null}
    </button>
  );

  const espreitada =
    ancora != null ? (
      <Espreitada
        item={item}
        ancora={ancora}
        aoTocar={() => {
          fechar();
          aoTocar();
        }}
        aoEntrar={() => {
          dentroDaEspreita.current = true;
        }}
        aoSair={() => {
          dentroDaEspreita.current = false;
          fechar();
        }}
      />
    ) : null;

  if (posto != null) {
    return (
      <div className="cine-posto">
        <span className="cine-posto-numero" aria-hidden="true">
          {posto}
        </span>
        {carta}
        {espreitada}
      </div>
    );
  }

  return (
    <>
      {carta}
      {espreitada}
    </>
  );
}

/* ── "Continuar assistindo" (`NetflixContinueCard`) ──────────────────── */

function CartaoContinua({ item, aoTocar }: { item: Cartaz; aoTocar: () => void }) {
  const capa = item.fundo || item.poster;
  const progresso = progressoDe(item);

  return (
    <button type="button" className="cine-cont" onClick={aoTocar}>
      <span className="cine-cont-capa">
        {capa ? (
          <img src={capa} alt={item.titulo} loading="lazy" decoding="async" />
        ) : (
          <span className="cine-sem-cartaz">
            <span>{item.titulo}</span>
          </span>
        )}
        <span className="cine-cont-veu" />
        <span className="cine-cont-linha">
          <b>{item.titulo}</b>
          <Icone nome="tocar-circulo" tamanho={26} />
        </span>
        <span className="cine-progresso">
          <i style={{ width: `${Math.min(1, Math.max(0, progresso)) * 100}%` }} />
        </span>
      </span>
      <span className="cine-cont-sub">{legendaDe(item)}</span>
    </button>
  );
}

/* ── A fileira (`netflix_row.dart`) ──────────────────────────────────── */

function useSetas(trilho: React.RefObject<HTMLDivElement | null>) {
  const [pode, setPode] = useState({ esq: false, dir: false });

  const medir = useCallback(() => {
    const el = trilho.current;
    if (!el) return;
    // ±4px de folga: o navegador arredonda a rolagem e a seta piscava sozinha.
    setPode({
      esq: el.scrollLeft > 4,
      dir: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, [trilho]);

  useLayoutEffect(() => {
    medir();
    const el = trilho.current;
    if (!el) return;
    el.addEventListener('scroll', medir, { passive: true });
    window.addEventListener('resize', medir);
    return () => {
      el.removeEventListener('scroll', medir);
      window.removeEventListener('resize', medir);
    };
  }, [medir, trilho]);

  const rolar = (lado: -1 | 1) => {
    const el = trilho.current;
    if (!el) return;
    // A seta foi apertada, então a fileira anda de todo jeito; para quem anda
    // menos ela chega de uma vez, porque o `behavior` daqui vence o
    // `scroll-behavior` que o `globais.css` desliga.
    const menos = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: lado * el.clientWidth, behavior: menos ? 'auto' : 'smooth' });
  };

  return { pode, rolar, medir };
}

interface PropsFila {
  titulo: string;
  itens: Cartaz[];
  aoTocar: (c: Cartaz) => void;
  /** Top 10: os dez primeiros ganham o número. */
  numerada?: boolean;
}

export function Fila({ titulo, itens, aoTocar, numerada }: PropsFila) {
  const trilho = useRef<HTMLDivElement>(null);
  const { pode, rolar, medir } = useSetas(trilho);

  useEffect(() => {
    medir();
  }, [itens, medir]);

  if (itens.length === 0) return null;

  return (
    <section className="cine-fila">
      <h2 className="cine-fila-titulo">{titulo}</h2>
      <button
        type="button"
        className={`cine-seta esq${pode.esq ? ' pode' : ''}`}
        onClick={() => rolar(-1)}
        aria-label={traduzir('Voltar')}
        tabIndex={-1}
      >
        <Icone nome="retroceder" tamanho={40} />
      </button>
      <div className="cine-trilho" ref={trilho}>
        {itens.map((c, i) => (
          <CartazDoFilme
            key={`${c.tmdbId}-${c.tipo}-${i}`}
            item={c}
            posto={numerada && i < 10 ? i + 1 : undefined}
            aoTocar={() => aoTocar(c)}
          />
        ))}
      </div>
      <button
        type="button"
        className={`cine-seta dir${pode.dir ? ' pode' : ''}`}
        onClick={() => rolar(1)}
        aria-label={traduzir('Avançar')}
        tabIndex={-1}
      >
        <Icone nome="avancar" tamanho={40} />
      </button>
    </section>
  );
}

export function FilaContinua({
  itens,
  aoTocar,
}: {
  itens: Cartaz[];
  aoTocar: (c: Cartaz) => void;
}) {
  const trilho = useRef<HTMLDivElement>(null);
  const { pode, rolar } = useSetas(trilho);

  if (itens.length === 0) return null;

  return (
    <section className="cine-fila cine-fila-continua">
      <h2 className="cine-fila-titulo">{traduzir('Continuar assistindo')}</h2>
      <button
        type="button"
        className={`cine-seta esq${pode.esq ? ' pode' : ''}`}
        onClick={() => rolar(-1)}
        aria-label={traduzir('Voltar')}
        tabIndex={-1}
      >
        <Icone nome="retroceder" tamanho={40} />
      </button>
      <div className="cine-trilho" ref={trilho}>
        {itens.map((c, i) => (
          <CartaoContinua key={`${c.doc ?? c.tmdbId}-${i}`} item={c} aoTocar={() => aoTocar(c)} />
        ))}
      </div>
      <button
        type="button"
        className={`cine-seta dir${pode.dir ? ' pode' : ''}`}
        onClick={() => rolar(1)}
        aria-label={traduzir('Avançar')}
        tabIndex={-1}
      >
        <Icone nome="avancar" tamanho={40} />
      </button>
    </section>
  );
}

/* ── O cartaz gigante (`netflix_billboard.dart`) ─────────────────────── */

export function PalcoGrande({
  itens,
  aoTocar,
  aoInfo,
}: {
  itens: Cartaz[];
  aoTocar: (c: Cartaz) => void;
  aoInfo: (c: Cartaz) => void;
}) {
  const tmdb = useContext(ContextoTMDB);
  const [i, setI] = useState(0);
  const [mudo, setMudo] = useState(true);
  const [pronto, setPronto] = useState(false);
  const { pausar } = usarTocador();

  const item = itens[i] ?? null;
  const chave = item ? `${item.tmdbId}:${item.tipo}` : '';
  const detalhes = useDetalhes(item, true);
  const [trailer, setTrailer] = useState<string | null>(() => trailersGuardados.get(chave) ?? null);

  // O ciclo: 14 segundos por título, como o `_hold`.
  useEffect(() => {
    if (itens.length < 2) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % itens.length), 14_000);
    return () => window.clearTimeout(t);
  }, [i, itens.length]);

  useEffect(() => {
    setPronto(false);
    if (!item || !tmdb) return;
    if (trailersGuardados.has(chave)) {
      setTrailer(trailersGuardados.get(chave) ?? null);
      setPronto(true);
      return;
    }
    let vivo = true;
    tmdb
      .trailer(item.tmdbId, item.tipo)
      .then((k) => {
        trailersGuardados.set(chave, k);
        if (vivo) {
          setTrailer(k);
          setPronto(true);
        }
      })
      .catch(() => {
        trailersGuardados.set(chave, null);
        if (vivo) {
          setTrailer(null);
          setPronto(true);
        }
      });
    return () => {
      vivo = false;
    };
  }, [chave, item, tmdb]);

  const capa = useMemo(() => (item ? item.fundo || item.poster : ''), [item]);

  if (!item) return null;

  const nota = Number(detalhes?.vote_average ?? item.nota ?? 0);
  const tempo = duracao(detalhes);
  const sinopse = item.sinopse || String(detalhes?.overview ?? '');

  return (
    <div className="cine-palco-grande">
      {trailer && pronto ? (
        <iframe
          key={trailer}
          className="cine-trailer"
          src={
            `https://www.youtube.com/embed/${trailer}` +
            `?autoplay=1&mute=${mudo ? 1 : 0}&controls=0&loop=1&playlist=${trailer}` +
            '&rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&disablekb=1&fs=0' +
            '&playsinline=1&enablejsapi=1'
          }
          title={traduzir('Trailer de {titulo}', { titulo: item.titulo })}
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : capa ? (
        <img key={capa} src={capa} alt="" />
      ) : null}

      <div className="cine-veu-baixo" />
      <div className="cine-veu-esq" />

      {i === 0 && itens.length > 1 ? (
        <div className="cine-top10">
          <b>{traduzir('TOP')}</b>
          <i>10</i>
        </div>
      ) : null}

      <div className="cine-palco-texto">
        <div className="cine-palco-meta">
          <span className="cine-combina">{combinacao(nota, item.tmdbId)}% Match</span>
          {item.ano ? <span>{item.ano}</span> : null}
          {tempo ? <span>{tempo}</span> : null}
          {item.tipo === 'movie' ? (
            <span className="cine-hd">{traduzir('HD')}</span>
          ) : (
            <span>{traduzir('Série')}</span>
          )}
        </div>
        <h1 className="cine-palco-titulo">{item.titulo}</h1>
        {sinopse ? <p className="cine-palco-sinopse">{sinopse}</p> : null}
        <div className="cine-palco-botoes">
          <button type="button" className="cine-botao forte" onClick={() => aoTocar(item)}>
            <Icone nome="tocar" preenchido />
            {traduzir('Assistir')}
          </button>
          <button type="button" className="cine-botao fraco" onClick={() => aoInfo(item)}>
            <Icone nome="info" />
            {traduzir('Mais info')}
          </button>
        </div>
      </div>

      {trailer ? (
        <button
          type="button"
          className="cine-mudo"
          onClick={() => {
            /* Um som de cada vez (ver `nucleo/tocador.tsx`). O trailer nasce
               mudo e vive num quadro do YouTube, que a página não escuta; a
               música só precisa calar no instante em que alguém liga o som dele
               -- e por isso aqui, e não na montagem da tela. */
            if (mudo) pausar();
            setMudo((m) => !m);
          }}
          aria-label={mudo ? traduzir('Ligar o som') : traduzir('Tirar o som')}
        >
          <Icone nome={mudo ? 'mudo' : 'som'} tamanho={18} />
        </button>
      ) : null}

      {itens.length > 1 ? (
        <div className="cine-pontos">
          {itens.map((c, n) => (
            <button
              key={`${c.tmdbId}-${n}`}
              type="button"
              className={`cine-ponto${n === i ? ' aqui' : ''}`}
              aria-current={n === i ? 'true' : undefined}
              onClick={() => setI(n)}
              aria-label={traduzir('Ver {titulo}', { titulo: c.titulo })}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ── Grade comum às abas ─────────────────────────────────────────────── */

export function Grade({ itens, aoTocar }: { itens: Cartaz[]; aoTocar: (c: Cartaz) => void }) {
  return (
    <div className="cine-grade">
      {itens.map((c, i) => (
        <CartazDoFilme
          key={`${c.doc ?? c.tmdbId}-${c.tipo}-${i}`}
          item={c}
          compacto
          progresso={c.status && c.status.startsWith('watching') ? progressoDe(c) : null}
          aoTocar={() => aoTocar(c)}
        />
      ))}
    </div>
  );
}

/* ── O recado flutuante (`ScaffoldMessenger.showSnackBar`) ───────────── */

/**
 * O `SnackBar` do Flutter não pertence a nenhuma tela: qualquer widget o pede
 * e ele aparece por cima de tudo. Aqui é o mesmo — quem avisa não precisa ter
 * um lugar na tela para o recado, e o cinema monta a barra uma vez só.
 */
const ouvintes = new Set<(texto: string) => void>();

/** Quanto o `SnackBar` fica na tela antes de sumir sozinho. */
const DEMORA = 4000;

/**
 * O último recado fica guardado por um instante.
 *
 * O `ScaffoldMessenger` do Flutter é de fora da tela, então um aviso dado no
 * mesmo gesto que fecha a sessão ainda aparece no cinema. Aqui a barra morre
 * com a tela, e sem esta espera o "fulano encerrou a sessão" — que é justamente
 * o recado dado ao sair — nunca seria lido por ninguém.
 */
let ultimo: { texto: string; quando: number } | null = null;

export function avisar(texto: string): void {
  ultimo = { texto, quando: Date.now() };
  for (const f of ouvintes) f(texto);
}

export function Recados() {
  const [texto, setTexto] = useState<string | null>(null);
  const barra = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const receber = (t: string) => setTexto(t);
    ouvintes.add(receber);
    /* Recado dado enquanto a barra ainda não existia — ou dado pela tela que
       estava saindo, que nunca chegou a desenhá-lo. */
    if (ultimo && Date.now() - ultimo.quando < DEMORA) setTexto(ultimo.texto);
    return () => {
      ouvintes.delete(receber);
    };
  }, []);

  useEffect(() => {
    if (texto === null) return;
    /* Desenhado é lido: a partir daqui a próxima tela não o repete. */
    if (ultimo?.texto === texto) ultimo = null;
    const relogio = window.setTimeout(() => setTexto(null), DEMORA);
    return () => window.clearTimeout(relogio);
  }, [texto]);

  /**
   * A barra sobe para a camada de cima do navegador.
   *
   * O `ScaffoldMessenger` de lá é de fora da tela e aparece por cima de tudo,
   * inclusive dos diálogos. Aqui um `<dialog>` aberto com `showModal()` vive
   * numa camada que nenhum `z-index` alcança — e o cinema tem três: a ficha de
   * um título, a gaveta de episódios e o tocador da TV ao vivo. Sem isto, todo
   * recado dado de dentro de um deles (o "Sessão aberta", o "Este canal não
   * está disponível agora") era desenhado atrás e ninguém lia.
   *
   * Navegador sem `popover` nunca ganha o atributo e a barra fica no fluxo,
   * com o `z-index` de sempre: é o que já acontecia, e não piora. O atributo é
   * posto aqui, e não no JSX, porque os tipos do React 18 ainda não o conhecem.
   */
  useEffect(() => {
    const b = barra.current;
    if (!b?.showPopover) return;
    b.setAttribute('popover', 'manual');
    b.showPopover();
    return () => {
      if (b.isConnected && b.matches(':popover-open')) b.hidePopover();
    };
  }, [texto]);

  if (texto === null) return null;
  return (
    <div ref={barra} className="cine-snack" role="status">
      {texto}
    </div>
  );
}
